/**
 * Briques d'interface communes.
 *
 * Le portail est un outil interne : pas de bibliothèque de composants, juste
 * quelques primitives Tailwind partagées pour que les écrans restent cohérents
 * (mêmes rayons, mêmes contrastes, même anneau de focus visible au clavier).
 */

import clsx from 'clsx';
import { Check, RefreshCw, X } from 'lucide-react';
import { Children, isValidElement } from 'react';
import type {
  ButtonHTMLAttributes,
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { Link } from 'react-router-dom';

/** Anneau de focus commun : indispensable pour la navigation au clavier. */
export const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ' +
  'focus-visible:outline-indigo-600 dark:focus-visible:outline-indigo-400';

// ---------------------------------------------------------------------------
// Structure
// ---------------------------------------------------------------------------

/** Encadré de contenu. */
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={clsx(
        'rounded-lg border border-slate-200 bg-white shadow-sm',
        'dark:border-slate-800 dark:bg-slate-900',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** En-tête de page : titre, description et actions alignées à droite. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
          {title}
        </h1>
        {description ? (
          <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/**
 * Conteneur de tableau large : le défilement horizontal reste à l'intérieur du
 * cadre, la page elle-même ne déborde jamais.
 */
export function TableScroll({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <div className="min-w-full align-middle">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contrôles
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-indigo-600 text-white hover:bg-indigo-500 disabled:bg-slate-300 ' +
    'dark:disabled:bg-slate-700 dark:disabled:text-slate-500',
  secondary:
    'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 ' +
    'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
  danger: 'bg-red-600 text-white hover:bg-red-500 disabled:bg-slate-300 dark:disabled:bg-slate-700',
  ghost:
    'text-slate-600 hover:bg-slate-100 hover:text-slate-900 ' +
    'dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
};

/** Bouton standard. */
export function Button({
  variant = 'secondary',
  loading = false,
  // Par défaut « button » et non « submit » : un bouton d'action posé dans un
  // formulaire de filtres ne doit pas le soumettre par accident.
  type = 'button',
  className,
  children,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-70',
        BUTTON_VARIANTS[variant],
        FOCUS_RING,
        className,
      )}
    >
      {loading ? (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />
          {/* Le spinner **remplace** l'icône du libellé au lieu de s'y ajouter.
              Presque tous les boutons de l'application s'écrivent
              `<Icône /> Texte` : sans ce filtrage, l'état de chargement en
              affichait deux côte à côte, l'une tournant et l'autre figée. */}
          {Children.toArray(children).filter((enfant) => !isValidElement(enfant))}
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Lien de navigation présenté comme un bouton.
 *
 * Un vrai `<a>`, pas un `<button>` qui appellerait `navigate` : le clic milieu,
 * l'ouverture dans un nouvel onglet et le survol qui montre la destination
 * doivent fonctionner. Les classes viennent de la même table que `Button`, pour
 * que les deux ne divergent pas visuellement — elles étaient jusqu'ici recopiées
 * à la main à chaque usage.
 */
export function LinkButton({
  variant = 'secondary',
  className,
  children,
  ...rest
}: ComponentProps<typeof Link> & { variant?: ButtonVariant }) {
  return (
    <Link
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
        'transition-colors',
        BUTTON_VARIANTS[variant],
        FOCUS_RING,
        className,
      )}
    >
      {children}
    </Link>
  );
}

/** Champ texte à une ligne. */
export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...rest}
      className={clsx(
        'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900',
        'placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
        FOCUS_RING,
        className,
      )}
    />
  );
}

/** Étiquette de champ, avec mention facultative. */
export function Label({
  htmlFor,
  children,
  hint,
  required = false,
}: {
  htmlFor: string;
  children: ReactNode;
  hint?: ReactNode;
  required?: boolean;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 flex items-baseline justify-between gap-3">
      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
        {children}
        {required ? <span className="ml-1 text-red-600 dark:text-red-400">*</span> : null}
      </span>
      {hint ? <span className="text-xs text-slate-500 dark:text-slate-400">{hint}</span> : null}
    </label>
  );
}

/** Liste déroulante. */
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...rest}
      className={clsx(
        'w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900',
        'dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100',
        FOCUS_RING,
        className,
      )}
    >
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Retours d'état
// ---------------------------------------------------------------------------

/** Bandeau d'erreur reprenant le message de l'API, sans le réécrire. */
export function ErrorBanner({
  message,
  issues,
  onRetry,
}: {
  message: string;
  issues?: Array<{ field?: string; message: string }>;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/60 dark:text-red-100"
    >
      <p className="font-medium">{message}</p>
      {issues && issues.length > 0 ? (
        <ul className="mt-2 list-inside list-disc space-y-1">
          {issues.map((issue, index) => (
            <li key={`${issue.field ?? 'issue'}-${index}`}>
              {issue.field ? <span className="font-mono text-xs">{issue.field} : </span> : null}
              {issue.message}
            </li>
          ))}
        </ul>
      ) : null}
      {onRetry ? (
        <Button variant="secondary" className="mt-3" onClick={onRetry}>
          Réessayer
        </Button>
      ) : null}
    </div>
  );
}

/** État vide, toujours accompagné d'une explication et d'une issue. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: ReactNode;
  action?: ReactNode;
}) {
  // Même fond que `Card` : posé à même le dégradé de la page, un encadré
  // simplement bordé n'était pas de la même famille que les panneaux voisins.
  // La bordure reste pointillée — c'est ce qui dit « rien ici », pas « contenu ».
  return (
    <div
      className={clsx(
        'rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm',
        'dark:border-slate-700 dark:bg-slate-900',
      )}
    >
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-600 dark:text-slate-400">
        {description}
      </p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * Squelette de chargement. Préféré à un « Chargement… » : la page garde sa
 * forme, l'œil n'a pas à se réorienter quand les données arrivent.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={clsx('animate-pulse rounded bg-slate-200 dark:bg-slate-800', className)}
    />
  );
}

/** Squelette de tableau : `rows` lignes de `columns` cellules. */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 p-4" role="status" aria-label="Chargement du tableau">
      {Array.from({ length: rows }, (_unused, rowIndex) => (
        <div key={rowIndex} className="flex gap-3">
          {Array.from({ length: columns }, (_unusedCell, columnIndex) => (
            <Skeleton key={columnIndex} className="h-5 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Teintes disponibles pour les pastilles et étiquettes. */
export type Tone = 'slate' | 'green' | 'amber' | 'red' | 'blue' | 'indigo';

const DOT_COLOURS: Record<Tone, string> = {
  slate: 'bg-slate-400',
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  blue: 'bg-sky-500',
  indigo: 'bg-indigo-500',
};

const BADGE_COLOURS: Record<Tone, string> = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  blue: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
};

/** Pastille de statut colorée. */
export function StatusDot({ tone }: { tone: Tone }) {
  return (
    <span
      aria-hidden="true"
      className={clsx('inline-block h-2.5 w-2.5 shrink-0 rounded-full', DOT_COLOURS[tone])}
    />
  );
}

/** Étiquette compacte. */
export function Badge({ children, tone = 'slate' }: { children: ReactNode; tone?: Tone }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        BADGE_COLOURS[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Retour visuel de copie : coche verte, croix rouge, ou rien. */
export function CopyFeedback({ status }: { status: 'idle' | 'copied' | 'error' }) {
  if (status === 'copied') {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
        <Check className="h-4 w-4" aria-hidden="true" />
        Copié
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        <X className="h-4 w-4" aria-hidden="true" />
        Copie impossible
      </span>
    );
  }
  return null;
}
